import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getRestaurantInfo, updateRestaurantInfo, uploadRestaurantPhoto, API_ORIGIN } from '../services/api';
import AppShell from '../components/layout/AppShell';
import { compressImage } from '../utils/compressImage';
import './RestaurantInfoForm.css';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DRAFT_KEY_PREFIX = 'restaurant_info_draft_';

const defaultHours = {
  monday: '9:00 AM - 9:00 PM',
  tuesday: '9:00 AM - 9:00 PM',
  wednesday: '9:00 AM - 9:00 PM',
  thursday: '9:00 AM - 9:00 PM',
  friday: '9:00 AM - 10:00 PM',
  saturday: '10:00 AM - 10:00 PM',
  sunday: 'Closed'
};

const defaultSocial = { facebook: '', instagram: '', twitter: '' };

const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/** Resolve user-facing message from API error (supports messageKey or raw string) */
function getErrorMessage(err, t, fallbackKey) {
  if (!err) return t(fallbackKey);
  if (!err.response) {
    if (err.message === 'Network Error') return t('errors.network');
    return t('errors.generic');
  }
  const apiError = err.response?.data?.error;
  if (typeof apiError === 'string') {
    if (apiError.includes('.')) {
      const translated = t(apiError);
      if (translated !== apiError) return translated;
    }
    return apiError;
  }
  const status = err.response?.status;
  if (status === 403) return t('errors.forbidden');
  if (status === 401) return t('errors.unauthorized');
  if (status >= 500) return t('errors.serverError');
  return t(fallbackKey);
}

const RestaurantInfoForm = () => {
  const { t } = useTranslation();
  const { user, restaurants, isManager, isVerified } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [form, setForm] = useState({
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    opening_hours: { ...defaultHours },
    photos: [],
    social_media: { ...defaultSocial },
    default_language: 'auto'
  });

  const restaurantId = restaurants?.length > 0 ? restaurants[0].id : null;

  const loadDraft = useCallback((id) => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY_PREFIX + id);
      if (raw) {
        const draft = JSON.parse(raw);
        setForm((prev) => ({
          ...prev,
          ...draft,
          opening_hours: { ...defaultHours, ...(draft.opening_hours || {}) },
          social_media: { ...defaultSocial, ...(draft.social_media || {}) }
        }));
      }
    } catch (_) {}
  }, []);

  const saveDraft = useCallback((id, data) => {
    try {
      localStorage.setItem(DRAFT_KEY_PREFIX + id, JSON.stringify(data));
    } catch (_) {}
  }, []);

  const fetchInfo = useCallback(async () => {
    if (!restaurantId) return;
    setFetchError(null);
    setLoading(true);
    try {
      const res = await getRestaurantInfo(restaurantId);
      const data = res.data;
      if (!data || typeof data !== 'object') {
        setFetchError(t('restaurant.info.errors.loadFailed'));
        loadDraft(restaurantId);
        return;
      }
      setForm({
        description: data.description || '',
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
        website: data.website || '',
        opening_hours: { ...defaultHours, ...(data.opening_hours && typeof data.opening_hours === 'object' ? data.opening_hours : {}) },
        photos: Array.isArray(data.photos) ? [...data.photos] : [],
        social_media: { ...defaultSocial, ...(data.social_media && typeof data.social_media === 'object' ? data.social_media : {}) },
        default_language: ['auto', 'en', 'es', 'ar'].includes(data.default_language) ? data.default_language : 'auto'
      });
      setFetchError(null);
    } catch (err) {
      setFetchError(getErrorMessage(err, t, 'restaurant.info.errors.loadFailed'));
      loadDraft(restaurantId);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, loadDraft, t]);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }
    fetchInfo();
  }, [restaurantId, fetchInfo]);

  useEffect(() => {
    if (!restaurantId || loading) return;
    const timer = setTimeout(() => saveDraft(restaurantId, form), 800);
    return () => clearTimeout(timer);
  }, [form, restaurantId, loading, saveDraft]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateHours = (day, value) => {
    setForm((prev) => ({
      ...prev,
      opening_hours: { ...prev.opening_hours, [day]: value }
    }));
  };

  const updateSocial = (key, value) => {
    setForm((prev) => ({
      ...prev,
      social_media: { ...prev.social_media, [key]: value }
    }));
  };

  const handleSave = async () => {
    if (!restaurantId) return;
    setSaving(true);
    setSnackbar((s) => ({ ...s, open: false }));
    try {
      await updateRestaurantInfo(restaurantId, {
        description: form.description.slice(0, 5000),
        address: form.address,
        phone: form.phone,
        email: form.email,
        website: form.website,
        opening_hours: form.opening_hours,
        photos: form.photos.slice(0, 5),
        social_media: form.social_media,
        default_language: form.default_language || 'auto'
      });
      setSnackbar({ open: true, message: t('restaurant.info.save'), severity: 'success' });
      setFetchError(null);
      try {
        localStorage.removeItem(DRAFT_KEY_PREFIX + restaurantId);
      } catch (_) {}
    } catch (err) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, t, 'restaurant.info.errors.saveFailed'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (restaurantId) window.open(`/menu/${restaurantId}/info`, '_blank');
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !restaurantId) return;
    if (form.photos.length >= 5) {
      setSnackbar({ open: true, message: t('restaurant.info.validation.maxPhotos'), severity: 'warning' });
      e.target.value = '';
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setSnackbar({ open: true, message: t('restaurant.info.errors.invalidImageType'), severity: 'error' });
      e.target.value = '';
      return;
    }

    // Downscale + recompress in the browser before uploading so large photos
    // don't trip the server's body-size limit (413). Falls back to a friendly
    // error if the image can't be decoded.
    let uploadFile = file;
    try {
      const { file: compressed } = await compressImage(file);
      uploadFile = compressed;
    } catch (err) {
      setSnackbar({ open: true, message: t('restaurant.info.errors.invalidImageType'), severity: 'error' });
      e.target.value = '';
      return;
    }

    // Safety net: even after compression, reject anything still over the cap.
    if (uploadFile.size > MAX_PHOTO_SIZE_BYTES) {
      setSnackbar({ open: true, message: t('restaurant.info.errors.fileTooBig'), severity: 'error' });
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('image', uploadFile);
    setUploading(true);
    setSnackbar((s) => ({ ...s, open: false }));
    try {
      const res = await uploadRestaurantPhoto(restaurantId, fd);
      const url = res.data?.photoUrl || res.data?.data?.photoUrl;
      if (url) {
        setForm((prev) => ({ ...prev, photos: [...prev.photos, url].slice(0, 5) }));
        setSnackbar({ open: true, message: t('restaurant.info.photoUploaded'), severity: 'success' });
      } else {
        setSnackbar({ open: true, message: t('restaurant.info.errors.uploadFailed'), severity: 'warning' });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: getErrorMessage(err, t, 'restaurant.info.errors.uploadFailed'),
        severity: 'error'
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removePhoto = (index) => {
    setForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  };

  if (!isManager() || !isVerified()) {
    return (
      <AppShell maxWidth={false}><div className="restaurant-info-form-page">
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('dashboard.manager.accessDenied.message')}</Typography>
          <Button component={Link} to="/manager/dashboard" sx={{ mt: 2 }} variant="contained">
            {t('common.back')} {t('nav.dashboard')}
          </Button>
        </Box>

      </div>
    </AppShell>
    );
  }

  if (!restaurantId) {
    return (
      <AppShell maxWidth={false}><div className="restaurant-info-form-page">
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('restaurant.info.errors.noRestaurant')}</Typography>
          <Button component={Link} to="/manager/dashboard" sx={{ mt: 2 }} variant="contained">
            {t('common.back')} {t('nav.dashboard')}
          </Button>
        </Box>

      </div>
    </AppShell>
    );
  }

  if (loading && !fetchError) {
    return (
      <AppShell maxWidth={false}><div className="restaurant-info-form-page">
        <div className="loading-container">
          <CircularProgress />
        </div>

      </div>
    </AppShell>
    );
  }

  if (fetchError) {
    return (
      <AppShell maxWidth={false}><div className="restaurant-info-form-page">
        <Box className="restaurant-info-form-container restaurant-info-form-error-state" sx={{ maxWidth: 720, mx: 'auto', p: 3, textAlign: 'center' }}>
          <Typography color="error" sx={{ mb: 2 }} component="p">
            {fetchError}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => fetchInfo()} disabled={loading}>
              {loading ? t('common.loading') : t('restaurant.info.errors.retry')}
            </Button>
            <Button component={Link} to="/manager/dashboard" variant="outlined">
              {t('common.back')} {t('nav.dashboard')}
            </Button>
          </Box>
        </Box>

      </div>
    </AppShell>
    );
  }

  const completionPercent = () => {
    let filled = 0;
    if (form.description?.trim()) filled++;
    if (form.address?.trim()) filled++;
    if (form.phone?.trim()) filled++;
    if (form.email?.trim()) filled++;
    if (form.website?.trim()) filled++;
    if (form.photos?.length > 0) filled++;
    const hasSocial = form.social_media && (form.social_media.facebook || form.social_media.instagram || form.social_media.twitter);
    if (hasSocial) filled++;
    return Math.min(100, Math.round((filled / 7) * 100));
  };

  return (
    <AppShell maxWidth={false}><div className="restaurant-info-form-page">
      <Box className="restaurant-info-form-container" sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('restaurant.info.edit')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('restaurant.info.profileComplete', { percent: completionPercent() })}
        </Typography>

        <Box component="form" noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label={t('restaurant.info.description')}
            multiline
            rows={4}
            value={form.description}
            onChange={(e) => updateField('description', e.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText={`${(form.description || '').length}/1000`}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel id="default-language-label">{t('restaurant.info.defaultLanguage')}</InputLabel>
            <Select
              labelId="default-language-label"
              label={t('restaurant.info.defaultLanguage')}
              value={form.default_language || 'auto'}
              onChange={(e) => updateField('default_language', e.target.value)}
            >
              <MenuItem value="auto">{t('restaurant.info.langAuto')}</MenuItem>
              <MenuItem value="en">{t('restaurant.info.langEnglish')}</MenuItem>
              <MenuItem value="es">{t('restaurant.info.langSpanish')}</MenuItem>
              <MenuItem value="ar">{t('restaurant.info.langArabic')}</MenuItem>
            </Select>
            <FormHelperText>{t('restaurant.info.defaultLanguageHelp')}</FormHelperText>
          </FormControl>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            {t('restaurant.info.contact')}
          </Typography>
          <TextField label={t('restaurant.info.phone')} value={form.phone} onChange={(e) => updateField('phone', e.target.value)} fullWidth />
          <TextField label={t('restaurant.info.email')} type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} fullWidth />
          <TextField label={t('restaurant.info.address')} multiline rows={2} value={form.address} onChange={(e) => updateField('address', e.target.value)} fullWidth />
          <TextField label={t('restaurant.info.website')} placeholder="https://" value={form.website} onChange={(e) => updateField('website', e.target.value)} fullWidth />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            {t('restaurant.info.hours')}
          </Typography>
          {DAY_KEYS.map((day) => (
            <Box key={day} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label={day.charAt(0).toUpperCase() + day.slice(1)}
                size="small"
                value={form.opening_hours[day] || ''}
                onChange={(e) => updateHours(day, e.target.value)}
                placeholder={t('restaurant.info.closed')}
                sx={{ flex: 1 }}
              />
            </Box>
          ))}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            {t('restaurant.info.photos')} (max 5)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            {form.photos.map((url, idx) => (
              <Box key={idx} className="restaurant-info-form-photo-thumb">
                <img src={url.startsWith('http') ? url : `${API_ORIGIN}${url}`} alt="" />
                <button type="button" onClick={() => removePhoto(idx)} className="restaurant-info-form-photo-remove" aria-label={t('common.delete')}>
                  ×
                </button>
              </Box>
            ))}
            {form.photos.length < 5 && (
              <label className="restaurant-info-form-upload-btn">
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handlePhotoUpload} disabled={uploading} style={{ display: 'none' }} />
                {uploading ? <CircularProgress size={24} /> : t('common.upload', 'Upload')}
              </label>
            )}
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            {t('restaurant.info.social')}
          </Typography>
          <TextField label="Facebook URL" value={form.social_media.facebook} onChange={(e) => updateSocial('facebook', e.target.value)} fullWidth size="small" />
          <TextField label="Instagram URL" value={form.social_media.instagram} onChange={(e) => updateSocial('instagram', e.target.value)} fullWidth size="small" />
          <TextField label="Twitter URL" value={form.social_media.twitter} onChange={(e) => updateSocial('twitter', e.target.value)} fullWidth size="small" />

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? t('common.loading') : t('restaurant.info.save')}
            </Button>
            <Button variant="outlined" onClick={handlePreview}>
              {t('restaurant.info.preview')}
            </Button>
            <Button component={Link} to="/manager/dashboard">
              {t('common.cancel')}
            </Button>
          </Box>
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
    </AppShell>
  );
};

export default RestaurantInfoForm;
